import { createApi } from '@reduxjs/toolkit/query/react';
import { get } from 'lodash-es';
import { format } from 'url';

import { RootStateType } from '@store/store';

import { TagTypesEnum } from '@enum/tag-types.enum';
import {
  AddConsumerInterface,
  ConsumerInterface,
  ConsumerMetaInterface,
  DeleteConsumerInterface,
} from '@interface/consumer/consumer.interface';
import { CredentialInterface } from '@interface/credential/credential.interface';
import { PaginationRequestInterface, ResponseDataInterface } from '@interface/logs/response-data.interface';
import { apiQueryAuth } from '@util/api/api-query-auth';
import { queryBuilder } from '@util/api/query-builder';

export const consumerApi = createApi({
  reducerPath: 'consumer-api',
  tagTypes: [TagTypesEnum.Consumer],
  baseQuery: apiQueryAuth('/consumers', true),
  endpoints: (build) => ({
    loadConsumers: build.query<ResponseDataInterface<ConsumerInterface[]>, void>({
      query: () => '',
      providesTags: [TagTypesEnum.Consumer],
    }),
    loadLimitConsumers: build.query<ResponseDataInterface<ConsumerInterface[]>, PaginationRequestInterface>({
      query: ({ pagination }) => format({ pathname: '/', query: queryBuilder(pagination) }),
    }),
    addConsumer: build.mutation<ResponseDataInterface<ConsumerInterface>, AddConsumerInterface>({
      query: (credentials) => ({
        url: '/',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: [TagTypesEnum.Consumer],
    }),
    loadConsumer: build.query<ResponseDataInterface<ConsumerInterface>, string>({
      query: (id: string) => `/${id}`,
      providesTags: [TagTypesEnum.Consumer],
    }),
    deleteConsumer: build.mutation<CredentialInterface, DeleteConsumerInterface>({
      query: ({ id, applicationId }) => ({
        url: `${id}?applicationId=${applicationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypesEnum.Consumer],
    }),
    getAccessToken: build.query<ConsumerMetaInterface, ConsumerInterface>({
      queryFn: async (consumer, api, _extraOptions, fetch) => {
        const token = (api.getState() as RootStateType).auth.data.token;
        const { apiKey } = ((api.getState() as RootStateType).credentials.data.at(0) as CredentialInterface) ?? {};

        const result = await fetch({
          url: `${import.meta.env.VITE_AUTH_SERVICE_BASE_URL}`,
          method: 'POST',
          headers: {
            authorization: `Bearer ${token}`,
          },
          body: {
            consumerId: consumer.id,
            apiKey: apiKey,
            scope: 'consumer',
          },
        });

        return {
          data: {
            accessToken: get(result.data as ResponseDataInterface<ConsumerMetaInterface>, ['data', 'accessToken']),
          },
        };
      },
    }),
  }),
});
